import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Grid } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import JsdhNotifikaceService from '../api/JsdhNotifikaceService/service/JsdhNotifikaceService';
import { QueryError } from '../api/QueryError';
import { Odpoved, Udalost, DisplejStylOdpovedi } from '../api/JsdhNotifikaceService/generated';

import { useZalohaJinde } from '../hooks/useZalohaJinde';
import { rozdelOdpovedi } from '../utils/rozdelOdpovedi';
import OdpovediHasicu from './OdpovediHasicu';
import StatistikyOdpovedi from './StatistikyOdpovedi';

interface OdpovediWrapperProps {
    aktivniPoplach: Udalost;
    deviceToken?: string;
    showSpecializace?: boolean;
    showDatumCasVyhlaseni?: boolean;
    displejStyl?: DisplejStylOdpovedi;
    adaptivniLayout?: boolean;
}

const OdpovediWrapper: React.FC<OdpovediWrapperProps> = ({ aktivniPoplach, deviceToken, showSpecializace = true, showDatumCasVyhlaseni = false, displejStyl = 'STRIPE', adaptivniLayout = false }) => {
    const poplachId = useMemo(() => aktivniPoplach?.id, [aktivniPoplach?.id]);

    const {
        data: odpovedi = [],
        isLoading,
        isFetched,
        error,
    } = useQuery<Odpoved[], QueryError>({
        queryKey: ['odpovedi', poplachId, deviceToken],
        queryFn: () =>
            poplachId
                ? JsdhNotifikaceService.getOdpovediByPoplachId(poplachId, deviceToken || undefined)
                : Promise.resolve([]),
        enabled: Boolean(poplachId),
        refetchInterval: 4000,
        refetchOnWindowFocus: false,
        staleTime: 3000,
    });

    // Odpovedi si wrapper taha sam, proto i zalohu z predchoziho poplachu (spec R6, § 5.2).
    const zalohaJinde = useZalohaJinde(poplachId, deviceToken);
    const rozdeleni = useMemo(() => rozdelOdpovedi(odpovedi, zalohaJinde), [odpovedi, zalohaJinde]);

    // Vyska oblasti karet: kiosek nescrolluje, co se nevejde, nahradi „+ X dalsich“ (0 = jeste nezmereno).
    const kartyRef = useRef<HTMLDivElement>(null);
    const [vyska, setVyska] = useState(0);
    useEffect(() => {
        const el = kartyRef.current;
        if (!el) return;
        setVyska(el.clientHeight);
        if (typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver(() => setVyska(el.clientHeight));
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const { jedou, nejedou, pocetVD, pocetS, pocetH } = useMemo(() => {
        let pocetVD = 0, pocetS = 0, pocetH = 0;

        // Role jen z tech, kdo jedou s vozem (spec R7); kazdy se pocita jednou.
        // Priorita: VD (Velitel druzstva) > S (Strojnik) > H (Hasic)
        for (const odpoved of rozdeleni.jedou) {
            if (!odpoved.funkce?.length) continue;
            const kody = odpoved.funkce.map(f => f.kod);
            if (kody.includes('VD')) {
                pocetVD++;
            } else if (kody.includes('S')) {
                pocetS++;
            } else if (kody.includes('H')) {
                pocetH++;
            }
        }

        return { jedou: rozdeleni.jedou.length, nejedou: rozdeleni.nejedou.length, pocetVD, pocetS, pocetH };
    }, [rozdeleni]);

    return (
        <Grid container spacing={0} sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, height: '100%' }}>
            {/* Statistiky */}
            <Grid size={{ xs: 12 }} sx={{ flexShrink: 0 }}>
                <StatistikyOdpovedi
                    pocetJedu={jedou}
                    pocetNejedu={nejedou}
                    pocetVD={pocetVD}
                    pocetS={pocetS}
                    pocetH={pocetH}
                    pocetZaloha={rozdeleni.zaloha.length}
                    showDatumCasVyhlaseni={showDatumCasVyhlaseni}
                    isLoading={!isFetched}
                    aktivniPoplach={aktivniPoplach}
                />
            </Grid>

            {/* Karty hasičů */}
            <Grid ref={kartyRef} size={{ xs: 12 }} sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                <OdpovediHasicu
                    odpovedi={odpovedi} test={false}
                    zalohaJinde={zalohaJinde}
                    showSpecializace={showSpecializace}
                    displejStyl={displejStyl}
                    adaptivniLayout={adaptivniLayout}
                    casVyhlaseni={aktivniPoplach.casVyhlaseni}
                    dostupnaVyska={vyska}
                />
            </Grid>
        </Grid>
    );
};

export default React.memo(OdpovediWrapper);
